import { FC, FunctionComponent, useState } from 'react';
import SubscriptionsRedemptionsTable from './subscriptionsRedemptionsTable.component';
import SubscriptionsRedemptionsChart from './subcsriptionsRedemptionsChart.component';
import { Button, Grid } from '@mui/material';
import { QueryBoundary } from '../../../utilityComponents/QueryBoundary';
import UltraTable, { TableData } from '../../../tables/ultraTable/UltraTable';
import { formatDateForCheckingState } from '../../../../utilities/dateFormatters';
import {
  ALL_SUB_RED_COLS,
  SubscriptionRedemptionData,
  buildColumns,
  buildData,
} from './utils/subscription-redemption-utils';
import { EditableFields } from '../../../tables/ultraTable/types/editable.types';
import { FundInfoComponentProps } from '../../../layout/general/SimpleGeneralPageWrapper';
import { useSubscriptionsRedemptionsPageModel } from '../../../../api/rs-hg/liquidity/hooks/queries';
import { SubscriptionsRedemptionsReport } from './subscriptionsRedemptions.types';

const SubscriptionsRedemptions: FC<FundInfoComponentProps> = (props) => {
  const { positionDate: activeDate, fundId } = props;

  const {
    data: subscriptionsRedemptionsPage,
    error,
    isError,
    isLoading,
    isFetching,
  } = useSubscriptionsRedemptionsPageModel(fundId, activeDate);

  return (
    <QueryBoundary<SubscriptionsRedemptionsReport>
      isLoading={isLoading}
      isFetching={isFetching}
      isError={isError}
      error={error}
      data={subscriptionsRedemptionsPage?.data}
      loadingMessages={['Loading Subscriptions/Redemptions Data...']}
      errorMessage="No Data Available."
      noDataMessage="No Data Available."
    >
      {(resolvedData) => {
        const absSubsRedsData = resolvedData.abs_subs_reds_data;
        const positionDate = resolvedData.selected_position_date;

        return (
          <Grid container>
            <SubscriptionsRedemptionsTable
              data={resolvedData}
              title={'Redemption Risk'}
            />
            <SubscriptionsRedemptionsChart
              data={resolvedData}
              title={'Redemption Data'}
            />
            <SubscriptionsRedemptionsTable
              data={resolvedData}
              title={'Gross Activity Risk'}
            />
            <SubscriptionsRedemptionsChart
              data={resolvedData}
              title={'Gross Activity Data'}
            />

            {absSubsRedsData && (
              <ButlerTableErrorBoundary
                fundId={fundId}
                positionDate={positionDate || ''}
                absSubsRedsData={absSubsRedsData}
              />
            )}
          </Grid>
        );
      }}
    </QueryBoundary>
  );
};

export default SubscriptionsRedemptions;

export interface SubscriptionRedemptionDataEditable
  extends SubscriptionRedemptionData,
  EditableFields { }

interface ButlerTableErrorBoundaryProps {
  absSubsRedsData: SubscriptionRedemptionData[];
  positionDate: string;
  fundId: string;
}

const ButlerTableErrorBoundary: FunctionComponent<
  ButlerTableErrorBoundaryProps
> = ({ absSubsRedsData, positionDate, fundId }) => {
  const [viewType, setViewType] = useState<
    'SUBSCRIPTION' | 'REDEMPTION' | null
  >(null);

  const data = absSubsRedsData.filter(
    (row: SubscriptionRedemptionData) =>
      viewType === null || row.type === viewType,
  );

  const tableData: TableData<any> = {
    columns: buildColumns(ALL_SUB_RED_COLS),
    data: buildData(data),
  };

  return (
    <UltraTable<SubscriptionRedemptionDataEditable>
      title="Subscriptions/Redemptions"
      tableData={tableData}
      getRowCanExpand={() => false}
      pdfConfig={{
        pdfIdentifier: `${fundId}_subscriptions_redemptions`,
        pdfTitle: `Subscriptions Redemptions Data - ${fundId}`,
        pdfExportGroupName: 'subscriptions_redemptions_page',
        pdfExportGroupOrder: 5,
        pdfFileName: `subscriptions-redemptions-${fundId}-${positionDate || formatDateForCheckingState(new Date())
          }`,
      }}
      actionBtns={[
        <Button
          key="subs"
          onClick={() =>
            setViewType((prev) =>
              prev === 'SUBSCRIPTION' ? null : 'SUBSCRIPTION',
            )
          }
          variant={viewType === 'SUBSCRIPTION' ? 'contained' : 'outlined'}
        >
          Subscriptions
        </Button>,
        <Button
          key="reds"
          onClick={() =>
            setViewType((prev) => (prev === 'REDEMPTION' ? null : 'REDEMPTION'))
          }
          variant={viewType === 'REDEMPTION' ? 'contained' : 'outlined'}
        >
          Redemptions
        </Button>,
      ]}
    />
  );
};
